import { useEffect, useState } from "react";

import { sendCoord, subscribeCoord } from "./channel";
import type { SessionState } from "./messages";

/** The viewer's live gating signal: content visibility + toolbar eligibility. */
export interface DocumentSessionState {
  sessionState: SessionState;
  /** Whether the opener currently holds the deal's VLA lock (gates the move/check-in toolbar). */
  heldByMe: boolean;
}

/**
 * Document-window-side session gate for the one-window-per-document viewer
 * (OR-22558). Each document window is self-sufficient for *which* document it
 * shows (via the URL + a `sessionStorage` handoff — see `openViewerWindow.ts`),
 * but still needs the opener's live session signal (auth + VLA lock) to know
 * whether it's still allowed to display content, and whether it should show
 * its move/check-in toolbar.
 *
 * Defaults optimistically to `sessionState: "active"` so a window renders its
 * document immediately rather than waiting on a round trip to an opener that
 * may already be closed (load-and-persist: a closed/backgrounded opener must
 * not blank a document the analyst is reading). `heldByMe` defaults to `false`
 * (the toolbar stays hidden until the opener confirms it's actually held) —
 * on mount it asks the opener for the current state via
 * `request-session-state`, correcting quickly either way.
 *
 * `session-state` broadcasts are stamped with the deal (`appId`) they belong
 * to — an analyst can have several deals open as separate Review tabs at
 * once, each broadcasting independently, so this window only ever adopts a
 * broadcast whose `appId` matches its own document's deal; broadcasts for a
 * different deal are ignored entirely (state is left unchanged). `heldByMe`
 * gates only the toolbar; the document itself stays viewable read-only even
 * once the lock is released. `session-ended` (logout) is a genuinely global
 * signal (not deal-scoped) and always applies, setting `sessionState:
 * "expired"` without touching `heldByMe`.
 * @param {string} appId - This window's own deal (approval id), from the URL.
 * @returns {DocumentSessionState} The most recently known session signal.
 */
export function useDocumentSessionState(appId: string): DocumentSessionState {
  const [state, setState] = useState<DocumentSessionState>({ sessionState: "active", heldByMe: false });

  useEffect(() => {
    const unsubscribe = subscribeCoord((message) => {
      if (message.type === "session-state") {
        if (message.appId !== appId) {
          return;
        }
        setState({ sessionState: message.sessionState, heldByMe: message.heldByMe });
      } else if (message.type === "session-ended") {
        setState((previous) => ({ ...previous, sessionState: "expired" }));
      }
    });

    // Handshake: ask the opener for the current session state.
    sendCoord({ type: "request-session-state" });

    return unsubscribe;
  }, [appId]);

  return state;
}
